import { FC, ReactElement, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import cn from 'classnames';
import { noop, reverse } from 'lodash';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  AddToProjectModal,
  AddToProjectModalContext,
  PageErrorMessage,
  PageTitle,
  SearchHistoryEntry,
  TimestampedRecordWithData,
} from '@app/components';
import { ROUTE } from '@app/constants';

import {
  DashboardSearchModal,
  DashboardSearchModalContext,
} from '../DashboardSearchModal';
import { TabsWithRouting } from '../TabsWithRouting';
import {
  OnSearchHistoryEntryRemoval,
  SearchHistoryList,
} from './SearchHistoryList';
import {
  DashboardInstancesList,
  onRecordRemoval,
} from './DashboardInstancesList';

import './HistoryRecordsPageWrapper.scss';

interface TabConfig {
  title: string;
  documentTitle: string;
  route: ROUTE;
  emptyStateMessage: string;
  emptyStateIcon: IconDefinition;
  isLoading?: boolean;
  isError?: boolean;
  refetch?: () => void;
}

interface SearchesTab extends TabConfig {
  list?: SearchHistoryEntry[];
  savedSearchesList?: SearchHistoryEntry[];
  itemSubtitle?: string;
  isItemRemovable?: boolean;
  onEntryRemoval: OnSearchHistoryEntryRemoval;
}

interface RecordsTab extends TabConfig {
  list?: TimestampedRecordWithData[];
  actionButton?: ReactElement;
  isItemRemovable?: boolean;
  onEntryRemoval: onRecordRemoval;
}

interface HistoryRecordsPageWrapperProps {
  parentRoute: ROUTE;
  searchesTabConfig: SearchesTab;
  recordsTabConfig: RecordsTab;
  isSearchesTabFirst?: boolean;
  pageTitle?: string;
}

const cnBem = createBemBlockBuilder(['history-records-page-wrapper']);

export const HistoryRecordsPageWrapper: FC<HistoryRecordsPageWrapperProps> = ({
  parentRoute,
  searchesTabConfig,
  recordsTabConfig,
  isSearchesTabFirst = true,
  pageTitle,
}) => {
  const { dispatch: updateAddToProjectModalState } = useContext(
    AddToProjectModalContext
  );
  const { dispatch: updateSearchModalState } = useContext(
    DashboardSearchModalContext
  );

  const getHelmet = (title: string) => (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );

  const tabConfig = [
    {
      title: searchesTabConfig.title,
      route: searchesTabConfig.route,
      component: (
        <>
          {getHelmet(searchesTabConfig.documentTitle)}
          {searchesTabConfig.isError ? (
            <PageErrorMessage onClick={searchesTabConfig.refetch} />
          ) : (
            <SearchHistoryList
              list={searchesTabConfig.list ?? []}
              itemSubtitle={searchesTabConfig.itemSubtitle}
              isLoading={searchesTabConfig.isLoading}
              emptyStateMessage={searchesTabConfig.emptyStateMessage}
              emptyStateIcon={searchesTabConfig.emptyStateIcon}
              savedSearchesList={searchesTabConfig.savedSearchesList ?? []}
              isItemRemovable={searchesTabConfig.isItemRemovable}
              onEntryRemoval={searchesTabConfig.onEntryRemoval}
              onAddToProject={noop}
              onViewInfo={record => {
                updateSearchModalState({
                  type: 'OPEN_MODAL',
                  payload: record,
                });
              }}
            />
          )}
        </>
      ),
    },
    {
      title: recordsTabConfig.title,
      route: recordsTabConfig.route,
      component: (
        <>
          {getHelmet(recordsTabConfig.documentTitle)}
          {recordsTabConfig.isError ? (
            <PageErrorMessage onClick={recordsTabConfig.refetch} />
          ) : (
            <DashboardInstancesList
              list={recordsTabConfig.list ?? []}
              isLoading={recordsTabConfig.isLoading}
              actionButton={recordsTabConfig.actionButton}
              isItemRemovable={recordsTabConfig.isItemRemovable}
              emptyStateMessage={recordsTabConfig.emptyStateMessage}
              emptyStateIcon={recordsTabConfig.emptyStateIcon}
              onEntryRemoval={recordsTabConfig.onEntryRemoval}
              onAddToProject={id =>
                updateAddToProjectModalState({
                  type: 'OPEN_MODAL',
                  payload: { id },
                })
              }
            />
          )}
        </>
      ),
    },
  ];

  return (
    <div className={cn(cnBem(), 'container')}>
      {pageTitle && (
        <div className={cnBem('__titleWrapper')}>
          <PageTitle>{pageTitle}</PageTitle>
        </div>
      )}
      <TabsWithRouting
        tabs={isSearchesTabFirst ? tabConfig : reverse(tabConfig)}
        parentRoute={parentRoute}
      />
      <AddToProjectModal />
      <DashboardSearchModal />
    </div>
  );
};
